import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import './AdvancedTreatmentTables.css';

const AdvancedTreatmentTables = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://www.primerehabindustries.in/#organization",
          "name": "Prime Rehab Industries",
          "url": "https://www.primerehabindustries.in",
          "logo": "https://www.primerehabindustries.in/logo.png",
          "telephone": "+919994351947",
          "email": "primerehaboffl@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "addressCountry": "IN"
          }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.primerehabindustries.in/#localbusiness",
          "name": "Prime Rehab Industries",
          "parentOrganization": { "@id": "https://www.primerehabindustries.in/#organization" },
          "url": "https://www.primerehabindustries.in",
          "telephone": "+919994351947",
          "email": "primerehaboffl@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "addressCountry": "IN"
          },
          "areaServed": "Tamil Nadu, India",
          "priceRange": "Contact for pricing"
        },
        {
          "@type": "Product",
          "name": "Physiotherapy Treatment Tables and Rehabilitation Equipment",
          "description": "Prime Rehab Industries manufactures physiotherapy treatment tables and rehabilitation equipment including high-low motorised tables, multi-fold treatment tables, traction tables, and tilt tables.",
          "brand": { "@id": "https://www.primerehabindustries.in/#organization" },
          "manufacturer": { "@id": "https://www.primerehabindustries.in/#organization" },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "142",
            "bestRating": "5"
          },
          "review": [
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Nandini Selvam" },
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "datePublished": "2025-01-14",
              "reviewBody": "Sturdy build, smooth height adjustment, and the face hole cutout is exactly the right size for prone positioning."
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Suresh Venkatraman" },
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "datePublished": "2025-07-30",
              "reviewBody": "Build quality across all pieces was consistent and delivery was on time. Remote control adjustment is a game-changer."
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Fixed-height vs. height-adjustable treatment tables?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Fixed-height tables sit at around 75 cm. Height-adjustable motorised tables allow therapists to set the correct working height for each patient, reducing strain and improving safety."
              }
            }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.primerehabindustries.in"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://www.primerehabindustries.in/blog"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Physiotherapy Treatment Tables and Rehab Equipment",
              "item": "https://www.primerehabindustries.in/blog/physiotherapy-treatment-tables-rehab-equipment"
            }
          ]
        }
      ]
    };

    document.title = "Advanced Treatment Tables & Rehab Equipment - Prime Rehab Industries";

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Prime Rehab Industries manufactures physiotherapy treatment tables and rehab equipment for clinics and hospitals across India with 12+ years of expertise.";

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.primerehabindustries.in/blog/physiotherapy-treatment-tables-rehab-equipment";

    let script = document.querySelector('#blog-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'blog-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.innerHTML = JSON.stringify(schema);

    return () => {};
  }, []);

  return (
    <div className="table-layout">
      <Navbar />

      {/* Hero Section */}
      <section className="table-hero">
        <div className="table-container">
          <div className="table-hero-grid">
            <div className="table-hero-content">
              <span className="table-tag">Clinical Equipment Guide</span>
              <h1>Advanced Physiotherapy Treatment Tables & Rehab Equipment</h1>
              <p>
                The foundation of every clinical setup. Match the right motorised specification to your therapy protocols and plan for long-term growth.
              </p>
              <div style={{ display: 'flex', gap: '16px' }}>
                <a href="/products" className="table-btn">View Table Catalogue</a>
                <a href="/contact" className="table-btn" style={{ background: '#fff', color: 'var(--table-heading)', border: '1px solid var(--table-border)' }}>Get Factory Pricing</a>
              </div>
            </div>
            <div className="table-hero-image-wrap">
              <img src="https://primerehabindustries.in/Image/Product/Pro8.png" alt="High Low 3-Fold Table by Prime Rehab Industries — Advanced physiotherapy treatment tables and rehab equipment manufacturer" />
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="table-section">
        <div className="table-container">
          <div className="table-section-header">
            <h2>The Foundation of Your Setup</h2>
            <p style={{ maxWidth: '800px' }}>
              Every physiotherapy session involves the treatment table. A table that is the wrong height forces you into postures that compound musculoskeletal risk. Getting the specification right is about matching clinical demands to your environment.
            </p>
          </div>

          <div className="table-vs-grid">
            <div className="table-vs-card">
              <h3>Fixed Height</h3>
              <p>Standard 75cm working height. Practical for solo practices with predictable patient profiles and lower volume.</p>
            </div>
            <div className="table-vs-circle">VS</div>
            <div className="table-vs-card">
              <h3>Motorised High-Low</h3>
              <p>Adjustable from 54cm to 102cm. Essential for safe wheelchair transfers, varying patient sizes, and high-volume clinical work.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Range Grid */}
      <section className="table-section" style={{ backgroundColor: '#f8fafc' }}>
        <div className="table-container">
          <div className="table-section-header">
            <h2>The Prime Rehab Table Range</h2>
          </div>
          <div className="table-specs-grid">
            <div className="table-spec-item">
              <h4>01. High-Low 3-Fold Table</h4>
              <p>Versatile three-section surface with remote control operation. Accommodates supine, prone, semi-reclined, and seated positions.</p>
            </div>
            <div className="table-spec-item">
              <h4>02. High-Low 2-Fold Table</h4>
              <p>Motorised height adjustment with a built-in breathing face hole cutout for prone positioning and spinal therapy.</p>
            </div>
            <div className="table-spec-item">
              <h4>03. Motorised Tilting Table</h4>
              <p>Designed for postural and neurological work, featuring motorised tilt, safety harnesses, and adjustable foot boards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial 1 */}
      <section className="table-quote">
        <div className="table-container">
          <blockquote>"The 3-fold table I ordered is sturdy, the height adjustment is smooth, and the face hole cutout is exactly the right size. Very happy with the purchase for my new clinic."</blockquote>
          <cite>— Nandini Selvam, Pondicherry</cite>
        </div>
      </section>

      {/* Specialized Section */}
      <section className="table-section">
        <div className="table-container">
          <div className="table-section-header">
            <h2>Specialised Clinical Solutions</h2>
          </div>
          <div className="table-vs-grid" style={{ gridTemplateColumns: '1.2fr 0.8fr', gap: '60px' }}>
            <div>
              <h3>Spinal & Chiropractic Focus</h3>
              <p>
                The **SPINETHERA DTS TABLE** (59–110 cm) provides a traction lock system and adjustable arm rests for controlled spinal decompression. 
              </p>
              <p>
                Our **Chiropractic Drop Table** is built specifically for adjustment techniques, with multi-section drop mechanisms and customisable resistance settings.
              </p>
            </div>
            <div className="table-spec-item">
              <h4>Key Stats</h4>
              <ul style={{ paddingLeft: '20px', lineHeight: '2' }}>
                <li>12+ Years Manufacturing</li>
                <li>1000+ Installations</li>
                <li>Heavy-Gauge Steel Frames</li>
                <li>High-Cycle Actuators</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial 2 */}
      <section className="table-quote" style={{ background: '#f8fafc', color: 'var(--table-heading)' }}>
        <div className="table-container">
          <blockquote>"Build quality was consistent across all pieces and delivery was on time. Our therapists particularly value the remote control height adjustment."</blockquote>
          <cite>— Suresh Venkatraman, SRM Medical College</cite>
        </div>
      </section>

      {/* Specialty Tips */}
      <section className="table-section">
        <div className="table-container">
          <div className="table-section-header">
            <h2>Choosing for Your Specialty</h2>
          </div>
          <div className="table-specs-grid">
            <div className="table-spec-item">
              <h4>General Outpatient</h4>
              <p>Prioritize a motorised 3-Fold or 2-Fold table. These provide the most clinical flexibility per therapy bay.</p>
            </div>
            <div className="table-spec-item">
              <h4>Orthopaedic Focus</h4>
              <p>Combine a standard high-low table with a dedicated traction or tilt table for decompression protocols.</p>
            </div>
            <div className="table-spec-item">
              <h4>Neurological Therapy</h4>
              <p>A wide-surface High Low Bobath Table (height from 48cm) is essential for neurodevelopmental handling techniques.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="table-faq-wrap">
        <div className="table-container">
          <div className="table-section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="table-faq-grid">
            <div className="table-faq-item">
              <h5>Fixed-height vs. Adjustable?</h5>
              <p>Adjustable motorised tables reduce therapist strain and allow for safe transfers from wheelchairs. They are the best long-term investment for growing practices.</p>
            </div>
            <div className="table-faq-item">
              <h5>How long do these tables last?</h5>
              <p>With heavy-gauge welded steel and clinical-rated actuators, our tables remain functional for many years. Only the upholstery may need eventual replacement.</p>
            </div>
            <div className="table-faq-item">
              <h5>Do they include face holes?</h5>
              <p>Yes, the Two Fold model includes a breathing face hole as standard. Other models can be configured based on your clinical needs.</p>
            </div>
            <div className="table-faq-item">
              <h5>Are they suitable for Chiropractic?</h5>
              <p>We manufacture a dedicated Chiropractic Drop Table with multi-section drops, specifically for adjustment techniques.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="table-cta">
        <div className="table-container">
          <h2>Equip Your Practice with the Best</h2>
          <p>Get factory pricing and expert advice on your clinic's equipment setup.</p>
          <a href="/contact" className="table-btn">Request Factory Quote</a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdvancedTreatmentTables;
